import styles from './sales-offer-table-renderers.module.scss';
import { calculatePriceToRenewal, calculateRecurringPrice, TextField, toCurrency } from '@frontend-workspace/react-shared-lib';
import { ValueFormatterParams } from 'ag-grid-community';
import { useEffect, useRef, useState } from 'react';
import { isNumerical } from '../../helpers';
import { IndicatorEnum } from '../../_models/product-group';

export interface DiscountRendererParams extends ValueFormatterParams {
  onBlur?: (value: number, itemInfoId: string) => void,
  disabled?: boolean,
};

export const useFlashing = (indicator: boolean, delay: number = 200, flashDuration: number = 700) => {
  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimerRef = useRef<NodeJS.Timeout | null>(null);
  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (indicator) {
      delayTimerRef.current = setTimeout(() => {
        setIsFlashing(true); // Start flashing after delay
        flashTimerRef.current = setTimeout(() => {
          setIsFlashing(false); // Stop flashing after the duration
        }, flashDuration);
      }, delay);
    }

    return () => { // Cleanup timers on unmount or when indicator changes
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
      setIsFlashing(false); // Ensure flashing stops when component unmounts or indicator changes
    };
  }, [indicator, delay, flashDuration]); // Only trigger when the indicator changes

  return isFlashing;
};

export const DiscountPercentRenderer = (params: DiscountRendererParams) => {
  const initialDiscountPercent = params.data.recurringDiscountPercent || 0;
  const [discountPercent, setDiscountPercent] = useState(initialDiscountPercent);
  const isFlashing = useFlashing(params.data.indicators?.includes(IndicatorEnum.RecurringDiscount));
  const onChange = (event: any) => setDiscountPercent(Number(event.target.value));
  const onBlur = () => {
    if (discountPercent !== initialDiscountPercent) {
      params.onBlur && params.onBlur(discountPercent, params.data.itemInfoId);
    }
  };

  return (
    <div className='d-flex align-items-center'>
      <TextField
        type="number"
        value={discountPercent.toFixed(0) || 0}
        disabled={params.disabled}
        error={discountPercent !== 0}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={event => { event.target.select() }}
        sx={{ width: 100 }}
        InputProps={{
          sx: {
            input: {
              textAlign: 'right',
              animation: isFlashing ? `${styles['flash']} 0.5s linear infinite` : 'none',
            }
          }
        }}
      />
    </div>
  );
};

export const OneTimeDiscountRenderer = (params: DiscountRendererParams) => {
  const initialDiscountPercent = params.data.oneTimeDiscountAmount || 0;
  const [discountPercent, setDiscountPercent] = useState(initialDiscountPercent);
  const isFlashing = useFlashing(params.data.indicators?.includes(IndicatorEnum.OneTimeDiscount));
  const onChange = (event: any) => setDiscountPercent(Number(event.target.value));
  const onBlur = () => {
    if (discountPercent !== initialDiscountPercent) {
      params.onBlur && params.onBlur(discountPercent, params.data.itemInfoId);
    }
  };

  return (
    <div className='d-flex align-items-center'>
      <TextField
        type="number"
        value={discountPercent.toFixed(0) || 0}
        disabled={params.disabled}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={event => { event.target.select() }}
        sx={{ width: 100 }}
        InputProps={{
          sx: {
            input: {
              textAlign: 'right',
              animation: isFlashing ? `${styles['flash']} 0.5s linear infinite` : 'none',
            }
          }
        }}
      />
    </div>
  );
};

export const RecurringPriceRenderer = (params: DiscountRendererParams) => {
  const calculateTotal = () =>
    calculateRecurringPrice(
      params.data.recurringDiscountPercent,
      params.value,
      params.data.newSeats,
    );

  const initialRecurringPrice = calculateTotal();
  const [recurringPrice, setRecurringPrice] = useState<number>(initialRecurringPrice);
  const [inputValue, setInputValue] = useState<string>(recurringPrice.toFixed(2));
  const isFlashing = useFlashing(params.data.indicators?.includes(IndicatorEnum.RecurringPrice));

  useEffect(() => {
    setRecurringPrice(calculateTotal());
    setInputValue(recurringPrice.toFixed(2));
  }, [params]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    if (isNumerical(rawValue)) {
      setInputValue(rawValue);
      setRecurringPrice(parseFloat(rawValue) || 0)
    }
  };

  const onBlur = () => {
    if (params.onBlur && recurringPrice !== initialRecurringPrice) {
      params.onBlur(recurringPrice, params.data.itemInfoId)
    }
  };

  return (
    <div className='d-flex align-items-center'>
      <TextField
        type="number"
        value={inputValue}
        disabled={params.disabled}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={event => { event.target.select() }}
        sx={{ width: 150 }}
        InputProps={{
          sx: {
            input: {
              textAlign: 'right',
              animation: isFlashing ? `${styles['flash']} 0.5s linear infinite` : 'none',
            },
          }
        }}
      />
    </div>
  );
};

export const PriceToRenewalRenderer = (params: DiscountRendererParams) => {
  const calculateTotal = () =>
    calculatePriceToRenewal(
      params.data.oneTimeDiscountAmount,
      params.value,
      params.data.newSeats,
    );

  const initialPriceToRenewal = calculateTotal();
  const [priceToRenewal, setPriceToRenewal] = useState<number>(initialPriceToRenewal);
  const [inputValue, setInputValue] = useState<string>(priceToRenewal.toFixed(2));
  const isFlashing = useFlashing(params.data.indicators?.includes(IndicatorEnum.PriceToRenewal));

  useEffect(() => {
    setPriceToRenewal(calculateTotal());
    setInputValue(priceToRenewal.toFixed(2));
  }, [params]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    if (!isNaN(parseFloat(rawValue))) {
      setInputValue(rawValue);
      setPriceToRenewal(parseFloat(rawValue) || 0);
    }
  };

  const onBlur = () => {
    if (params.onBlur && priceToRenewal !== initialPriceToRenewal) {
      params.onBlur(priceToRenewal, params.data.itemInfoId);
    }
  };

  return (
    <div className="d-flex align-items-center">
      <TextField
        type="number"
        value={inputValue}
        disabled={params.disabled}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={(event) => event.target.select()}
        sx={{ width: 150 }}
        InputProps={{
          sx: {
            input: {
              textAlign: 'right',
              animation: isFlashing ? `${styles['flash']} 0.5s linear infinite` : 'none',
            },
          },
        }}
      />
    </div>
  );
};

export const customerRecurringPriceRenderer = (params: DiscountRendererParams) => {
  const total = calculateRecurringPrice(
    params.data.recurringDiscountPercent,
    params.value,
    params.data.newSeats,
  );

  return total > 0 ? toCurrency(total) : 0;

}
export const customerPriceToRenewalRenderer = (params: DiscountRendererParams) => {
  const total = calculatePriceToRenewal(
    params.data.oneTimeDiscountAmount,
    params.value,
    params.data.newSeats,
  );

  return total > 0 ? toCurrency(total) : 0;
}